/**
 * CryptidZoo Asset Catalog
 * 
 * Defines all assets available in the game.
 * This catalog is used by the AssetManager to load and configure assets.
 * 
 * HOW TO ADD NEW ASSETS:
 * 1. Place your asset file in the appropriate public/assets/ subdirectory
 * 2. Run the model analysis tool to get dimensions (see ENGINE_GUIDE.md)
 * 3. Add an entry to the appropriate section below (models, textures, etc.)
 * 4. Set the scale to normalize the model to world units (meters)
 * 
 * SCALING GUIDE:
 * - Most 3D models from Blender/Maya export in centimeters or with varying scales
 * - Use analyzeModel() to determine the original size
 * - Calculate scale: targetHeight / originalHeight
 * - Common scales:
 *   - Blender (default): often 1.0 or 0.01 depending on export settings
 *   - Mixamo characters: usually around 0.01
 *   - Unity assets: often 0.01 to 0.1
 * 
 * ASSET PATHS:
 * - All paths are relative to public/assets/
 * - Models: entities/<EntityName>/<ModelName>.glb
 * - Textures: textures/<TextureName>.png
 */

import { AssetCatalog, AssetType, CollisionGroups } from "@engine/index";

/**
 * Game asset catalog - all available assets
 */
export const GameAssets: AssetCatalog = {
    models: {
        // ============================================
        // CRYPTIDS (Main characters/creatures)
        // ============================================
        
        /**
         * Sasquatch - Forest cryptid
         * 
         * To determine correct scale:
         * 1. The analyzeModel() function will print original dimensions
         * 2. Divide target height by original height
         * 3. For a 3-meter tall Sasquatch with original height of 200 units: 3/200 = 0.015
         * 
         * Adjust the scale value below based on your model's actual dimensions
         */
        sasquatch: {
            id: "sasquatch",
            type: AssetType.MODEL,
            path: "entities/Sasquatch/Sasquatch.glb",
            name: "Sasquatch",
            // SCALE: Set to 1.0 initially to see the model's true size
            // Then adjust based on analyzeModel() console output
            // Common values:
            //   - If model height is ~200: scale = 0.015 for 3m result
            //   - If model height is ~2 (already meters): scale = 1.5 for 3m result  
            //   - If model height is ~1: scale = 3.0 for 3m result
            scale: 1.0, // START AT 1.0 - check console for actual size, then adjust
            physics: {
                enabled: true,
                shapeType: "capsule",
                mass: 100,
                friction: 0.6,
                restitution: 0.1,
                boundingBox: {
                    width: 1.0,  // ~1 meter wide
                    height: 3.0, // 3 meters tall
                    depth: 0.8,  // ~0.8 meter deep
                    offset: { x: 0, y: 1.5, z: 0 }, // Center at half height
                },
                collisionGroup: CollisionGroups.NPC,
                collisionMask: CollisionGroups.TERRAIN | CollisionGroups.ENTITY | CollisionGroups.PLAYER,
            },
            animations: {
                defaultAnimation: "idle",
                loop: true,
                speed: 1.0,
                mappings: {
                    // Map friendly names to actual animation names in the GLB
                    // Check console output for actual animation names
                    idle: "Idle",
                    walk: "Walk", 
                    run: "Run",
                    attack: "Attack",
                },
            },
            metadata: {
                category: "cryptid",
                habitat: "forest",
                rarity: "rare",
                description: "A large, hairy, bipedal humanoid creature",
            },
        },

        // ============================================
        // TEMPLATE: Copy this for new models
        // ============================================
        /*
        modelId: {
            id: "modelId",
            type: AssetType.MODEL,
            path: "entities/ModelFolder/Model.glb",
            name: "Model Name",
            scale: 1.0, // Adjust based on analyzeModel() output
            physics: {
                enabled: true,
                shapeType: "capsule", // box, sphere, capsule, cylinder, mesh, convex
                mass: 50,
                friction: 0.5,
                restitution: 0.2,
                boundingBox: {
                    width: 1,
                    height: 2,
                    depth: 1,
                },
            },
            animations: {
                defaultAnimation: "idle",
                loop: true,
                speed: 1.0,
            },
            metadata: {
                category: "creature",
            },
        },
        */
    },

    textures: {
        // ============================================
        // TEXTURES
        // ============================================
        
        // Example texture entry
        // grassDiffuse: {
        //     id: "grassDiffuse",
        //     type: AssetType.TEXTURE,
        //     path: "textures/grass_diffuse.png",
        //     name: "Grass Diffuse",
        //     generateMipMaps: true,
        //     filtering: "trilinear",
        // },
    },
};

/**
 * Get model definition by ID
 */
export function getModelDefinition(id: string) {
    return GameAssets.models[id] || null;
}

/**
 * Get all model IDs
 */
export function getAllModelIds(): string[] {
    return Object.keys(GameAssets.models);
}

/**
 * Get models by category
 */
export function getModelsByCategory(category: string) {
    return Object.values(GameAssets.models).filter(
        model => model.metadata?.category === category
    );
}
